/*
 * Search, in the top bar — M-25, closing CO2 §2.2's first clause.
 *
 * ── Why it left the sidebar ─────────────────────────────────────────────────────────────────────
 *
 * It sat between Interviews and Team as though it were a sixth destination. It is not a
 * destination: nobody sets out to *go to search*, they set out to find a person, and the sidebar
 * shape made that two clicks — one to arrive at an empty box, one to use it. Here it is one field
 * on every screen, and the results page it opens is still a page somebody can link to.
 *
 * ── What it does not do, and why ────────────────────────────────────────────────────────────────
 *
 * **It does not register the `/` shortcut.** Every list screen in this app already binds `/` to its
 * own filter box through `useFocusShortcut('search', …)`, and the registry keys handlers by action
 * — so a global registration here would silently win on every one of those screens, and pressing
 * `/` on the candidates list would take the caret out of the filter a recruiter was about to type
 * in. A control that is visible on every screen does not need a shortcut badly enough to break the
 * one that is already there.
 *
 * **It carries no results of its own.** No dropdown, no preview. The results screen is where the
 * facets, the interpretation chips and the saved-view machinery live, and a second half-featured
 * result surface in the header would be a place people learn to distrust.
 *
 * The field is gated on the same entitlement and permission as the route it opens — checked here
 * rather than assumed, because the header renders for every principal and a box that leads to a
 * 403 is worse than no box.
 */

import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Input } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';
import { can, hasEntitlement, useSession } from '../auth/session.js';

export default function TopSearch() {
  const { t } = useI18n();
  const { me } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const field = useRef<HTMLInputElement>(null);

  if (!hasEntitlement(me, 'candidates') || !can(me, 'read', 'candidate')) {
    return null;
  }

  return (
    <form
      className="tf-top-search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const term = query.trim();
        /*
         * An empty submit opens the screen rather than doing nothing. Somebody who hits return on
         * an empty box wants the search surface, and the alternative — a form that visibly
         * refuses — teaches them the control is broken.
         */
        void navigate({ to: '/search', search: term ? { q: term } : {} });
        field.current?.blur();
      }}
    >
      <Input
        ref={field}
        type="search"
        value={query}
        aria-label={t('search.nav')}
        placeholder={t('search.top.placeholder')}
        onChange={(event) => setQuery(event.currentTarget.value)}
      />
    </form>
  );
}
